import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar as ShadCal } from "@/components/ui/calendar";
import {
  Users, UserPlus, ChalkboardTeacher, BookOpen, CalendarBlank, Clock,
  Cake, Bell, TrendUp, TrendDown, CurrencyDollar, ChartPieSlice, Lightning,
  UserCircle, DotsThreeVertical, ArrowUpRight, Trophy, Sparkle,
} from "@phosphor-icons/react";
import { getTheme } from "@/lib/certificateTemplates";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend, Area, AreaChart,
} from "recharts";
import { cn } from "@/lib/utils";

const fmt$ = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function KpiCard({ label, value, icon: Icon, delta, tone = "default", testId, sub }) {
  const tones = {
    default: "bg-secondary text-foreground",
    red: "bg-destructive/10 text-destructive",
    navy: "bg-primary/10 text-primary",
    green: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  };
  return (
    <Card data-testid={testId} className="card-hover border-border/60">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className={cn("w-10 h-10 rounded-md flex items-center justify-center", tones[tone])}>
            <Icon size={20} weight="duotone" />
          </div>
          {delta != null && (
            <div className={cn(
              "flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded",
              delta >= 0 ? "text-emerald-600 bg-emerald-500/10" : "text-destructive bg-destructive/10"
            )}>
              {delta >= 0 ? <TrendUp size={12} /> : <TrendDown size={12} />}
              {Math.abs(delta)}%
            </div>
          )}
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">{label}</div>
          <div className="text-3xl font-bold font-mono mt-1" data-num>{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function SectionCard({ title, action, children, className, testId }) {
  return (
    <Card data-testid={testId} className={cn("border-border/60", className)}>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [payChart, setPayChart] = useState([]);
  const [attChart, setAttChart] = useState([]);
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [recent, setRecent] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activities, setActivities] = useState([]);
  const [alumni, setAlumni] = useState([]);
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/student-growth"),
      api.get("/dashboard/payments-chart"),
      api.get("/dashboard/attendance-chart"),
      api.get("/dashboard/upcoming-lessons"),
      api.get("/dashboard/upcoming-birthdays"),
      api.get("/dashboard/recent-registrations"),
      api.get("/dashboard/teacher-schedule"),
      api.get("/notifications"),
      api.get("/activities"),
      api.get("/dashboard/alumni"),
    ]).then(([s, g, p, a, ul, bd, rc, ts, nt, ac, al]) => {
      setStats(s.data); setGrowth(g.data); setPayChart(p.data); setAttChart(a.data);
      setUpcomingLessons(ul.data); setBirthdays(bd.data); setRecent(rc.data);
      setTeachers(ts.data); setNotifications(nt.data); setActivities(ac.data);
      setAlumni(al.data);
    }).catch(console.error);
  }, []);

  const attData = stats ? Object.entries(stats.attendance_summary).map(([k, v]) => ({ name: k, value: v })) : [];
  const PIE_COLORS = ["hsl(var(--chart-1))", "hsl(var(--destructive))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("dashboard.title")}</div>
          <h1 className="text-3xl md:text-4xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>
            {t("dashboard.welcomeBack", { name: user?.name?.split(" ")[0] || "" })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("dashboard.overview")}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="qa-add-student" variant="outline" className="rounded-full gap-2">
            <UserPlus size={16} weight="duotone" /> {t("students.addStudent")}
          </Button>
          <Button data-testid="qa-new-lesson" className="rounded-full gap-2">
            <Lightning size={16} weight="duotone" /> {t("common.actions")}
          </Button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard testId="kpi-total-students" label={t("dashboard.totalStudents")} value={stats?.total_students ?? 0} icon={Users} delta={8} tone="navy" />
        <KpiCard testId="kpi-active-students" label={t("common.active") + " " + t("nav.students")} value={stats?.active_students ?? 0} icon={UserCircle} tone="green" />
        <KpiCard testId="kpi-new-registrations" label={t("dashboard.recentRegistrations")} value={stats?.new_registrations ?? 0} icon={UserPlus} delta={12} tone="red" />
        <KpiCard testId="kpi-teachers" label={t("nav.teachers")} value={stats?.teachers ?? 0} icon={ChalkboardTeacher} tone="navy" />
        <KpiCard testId="kpi-courses" label={t("nav.courses")} value={stats?.courses ?? 0} icon={BookOpen} tone="amber" />
        <KpiCard testId="kpi-classes" label={t("nav.classes")} value={stats?.classes ?? 0} icon={CalendarBlank} tone="green" />
      </div>

      {/* Revenue KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard testId="kpi-today-lessons" label="Today's Lessons" value={stats?.todays_lessons ?? 0} icon={Clock} tone="navy" sub="Scheduled today" />
        <KpiCard testId="kpi-pending-payments" label="Pending Payments" value={fmt$(stats?.pending_payments_amount)} icon={CurrencyDollar} tone="red" sub={`${stats?.pending_payments_count ?? 0} invoices`} />
        <KpiCard testId="kpi-monthly-income" label="Monthly Income" value={fmt$(stats?.monthly_income)} icon={TrendUp} delta={14} tone="green" />
        <KpiCard testId="kpi-yearly-revenue" label="Yearly Revenue" value={fmt$(stats?.yearly_revenue)} icon={ChartPieSlice} tone="amber" sub="YTD" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Student growth */}
        <SectionCard testId="chart-student-growth" title="Student Growth" className="lg:col-span-2"
          action={<span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Last 12 months</span>}
        >
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <AreaChart data={growth}>
                <defs>
                  <linearGradient id="growthG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="total" stroke="hsl(var(--chart-1))" fill="url(#growthG)" strokeWidth={2.5} />
                <Line type="monotone" dataKey="new" stroke="hsl(var(--destructive))" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        {/* Attendance donut */}
        <SectionCard testId="chart-attendance" title="Attendance Summary"
          action={<span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Today</span>}
        >
          <div className="h-64 w-full flex items-center">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <PieChart>
                <Pie data={attData} innerRadius={45} outerRadius={80} dataKey="value" paddingAngle={3}>
                  {attData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard testId="chart-payments" title="Payments · Paid vs Pending" className="lg:col-span-2"
          action={<span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">6 months</span>}
        >
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={payChart}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="paid" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="pending" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard testId="chart-attendance-week" title="Attendance This Week"
          action={<span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Daily</span>}
        >
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <LineChart data={attChart}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Line dataKey="present" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot />
                <Line dataKey="absent" stroke="hsl(var(--destructive))" strokeWidth={2} dot />
                <Line dataKey="late" stroke="hsl(var(--chart-4))" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* Bottom grid — lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Registrations */}
        <SectionCard testId="list-recent-registrations" title="Recent Registrations"
          action={<Button variant="ghost" size="sm" className="text-xs gap-1">View all <ArrowUpRight size={12} /></Button>}
        >
          <ul className="space-y-3">
            {recent.slice(0, 5).map((s) => (
              <li key={s.id} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                  {s.photo ? <img src={s.photo} alt="" className="w-full h-full object-cover" /> :
                    <span className="text-xs font-bold">{s.full_name?.charAt(0)}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{s.full_name}</div>
                  <div className="text-[11px] text-muted-foreground">{s.student_number} · {s.english_level}</div>
                </div>
                <span className="text-[10px] text-muted-foreground font-mono">
                  {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
              </li>
            ))}
          </ul>
        </SectionCard>

        {/* Upcoming Birthdays */}
        <SectionCard testId="list-birthdays" title="Upcoming Birthdays"
          action={<Cake size={16} weight="duotone" className="text-destructive" />}
        >
          <ul className="space-y-3">
            {birthdays.slice(0, 5).map((b) => (
              <li key={b.id} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0 font-bold text-sm">
                  {b.full_name?.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{b.full_name}</div>
                  <div className="text-[11px] text-muted-foreground">Turning {b.age_turning}</div>
                </div>
                <div className={cn(
                  "text-[10px] font-bold px-2 py-1 rounded font-mono",
                  b.days_until === 0 ? "bg-destructive text-destructive-foreground" : "bg-secondary text-foreground"
                )}>
                  {b.days_until === 0 ? "TODAY" : `${b.days_until}d`}
                </div>
              </li>
            ))}
            {birthdays.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No birthdays coming up</div>}
          </ul>
        </SectionCard>

        {/* Upcoming Lessons */}
        <SectionCard testId="list-upcoming-lessons" title="Upcoming Lessons"
          action={<Clock size={16} weight="duotone" className="text-primary" />}
        >
          <ul className="space-y-3">
            {upcomingLessons.slice(0, 5).map((l) => (
              <li key={l.id} className="flex gap-3">
                <div className="text-center w-11 shrink-0">
                  <div className="text-[10px] uppercase text-muted-foreground font-bold">
                    {new Date(l.date).toLocaleDateString(undefined, { month: "short" })}
                  </div>
                  <div className="text-lg font-bold font-mono leading-none">{new Date(l.date).getDate()}</div>
                </div>
                <div className="min-w-0 flex-1 border-l border-border pl-3">
                  <div className="text-sm font-semibold truncate">{l.title}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {l.start_time} · {l.room || "TBD"}
                  </div>
                </div>
              </li>
            ))}
            {upcomingLessons.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No upcoming lessons</div>}
          </ul>
        </SectionCard>
      </div>

      {/* Teacher schedule + Calendar + Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard testId="list-teacher-schedule" title="Teacher Schedule">
          <ul className="space-y-3">
            {teachers.slice(0, 6).map((t) => (
              <li key={t.id} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0">
                  {t.full_name?.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{t.full_name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{t.subject}</div>
                </div>
                <span className="text-xs font-mono font-bold">{t.lessons_today} <span className="text-muted-foreground font-normal">today</span></span>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard testId="dashboard-calendar" title="Calendar">
          <div className="flex justify-center">
            <ShadCal mode="single" selected={date} onSelect={setDate} className="rounded-md" />
          </div>
        </SectionCard>

        <SectionCard testId="list-activities" title="Latest Activities"
          action={<span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Live</span>}
        >
          <ul className="space-y-4 max-h-72 overflow-y-auto scrollbar-thin pr-1">
            {activities.map((a, i) => (
              <li key={a.id} className="flex gap-3">
                <div className="relative shrink-0">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold">
                    {a.actor?.charAt(0)}
                  </div>
                  {i < activities.length - 1 && <div className="absolute left-1/2 top-8 w-px h-6 bg-border -translate-x-1/2" />}
                </div>
                <div className="min-w-0 flex-1 pb-1">
                  <div className="text-sm leading-snug">
                    <span className="font-semibold">{a.actor}</span>{" "}
                    <span className="text-muted-foreground">{a.action}</span>{" "}
                    {a.target && <span className="font-semibold brand-red">{a.target}</span>}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(a.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      {/* Alumni Wall */}
      {alumni.length > 0 && (
        <SectionCard testId="alumni-wall" title="Alumni Wall · Gold & Platinum"
          action={<div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <Trophy size={12} weight="fill" className="text-amber-500" /> Latest recipients
          </div>}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {alumni.slice(0, 6).map((a) => {
              const t = getTheme(a.template);
              return (
                <div key={a.id} data-testid={`alumni-${a.id}`}
                  className="p-4 rounded-md text-center card-hover"
                  style={{
                    background: `linear-gradient(135deg, ${t.bgFrom}, ${t.bgTo})`,
                    borderTop: `3px solid ${t.accent}`,
                  }}>
                  <div className="w-14 h-14 mx-auto rounded-full border-2 overflow-hidden flex items-center justify-center text-2xl"
                    style={{ borderColor: t.accent, background: t.accent, color: t.bgFrom }}>
                    {a.student_photo ? <img src={a.student_photo} alt="" className="w-full h-full object-cover" /> : t.seal}
                  </div>
                  <div className="mt-2 text-sm font-semibold truncate" style={{ color: t.textOn, fontFamily: "Manrope" }}>
                    {a.student_name}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-bold mt-1" style={{ color: t.accent }}>
                    {t.label} · {a.level || t.tag}
                  </div>
                  <div className="text-[10px] mt-1 opacity-70" style={{ color: t.textOn }}>
                    {a.title}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Notifications feed at bottom (quick view) */}
      <SectionCard testId="dashboard-notifications" title="Notification Center"
        action={<Bell size={16} weight="duotone" className="text-destructive" />}
      >
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {notifications.slice(0, 6).map((n) => (
            <li key={n.id} className="p-3 rounded-md border border-border/60 hover:border-foreground/30 transition-colors">
              <div className="flex items-start gap-2">
                <span className={cn("w-2 h-2 mt-2 rounded-full shrink-0", !n.read ? "bg-destructive" : "bg-muted")} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{n.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}
